import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Checkbox } from '../ui/checkbox';
import { ArrowLeft, Save, Eye, Upload, Image as ImageIcon, X, Loader2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const CATEGORIES = [
  'Case Study · Wastewater Pump Station',
  'Case Study · Water Treatment',
  'Case Study · Oil & Gas',
  'Case Study · Power Generation',
  'Case Study · Municipal Infrastructure',
  'Case Study · Manufacturing',
  'Case Study · Marine',
  'Case Study · Mining',
  'Emergency Repair',
  'Preventative Maintenance',
  'Featured Project',
];

const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];
const currentYear = new Date().getFullYear();
const YEARS = [currentYear - 1, currentYear, currentYear + 1];

// Downscale a file to a JPEG data URL (max 1600px, 82% quality) — same
// approach the RichTextEditor uses so email HTML stays compact.
const fileToDataUrl = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onerror = () => reject(new Error('Could not read image'));
  reader.onload = () => {
    const img = new window.Image();
    img.onerror = () => reject(new Error('Invalid image'));
    img.onload = () => {
      const MAX = 1600;
      let { width, height } = img;
      if (width > MAX || height > MAX) {
        const r = Math.min(MAX / width, MAX / height);
        width = Math.round(width * r); height = Math.round(height * r);
      }
      const canvas = document.createElement('canvas');
      canvas.width = width; canvas.height = height;
      canvas.getContext('2d').drawImage(img, 0, 0, width, height);
      resolve(canvas.toDataURL('image/jpeg', 0.82));
    };
    img.src = reader.result;
  };
  reader.readAsDataURL(file);
});

const PhotoDrop = ({ slot, label, value, onChange, testid }) => {
  const inputRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);

  const takeFile = useCallback(async (file) => {
    if (!file || !file.type?.startsWith('image/')) {
      toast.error('Please choose an image file');
      return;
    }
    try {
      const dataUrl = await fileToDataUrl(file);
      onChange(dataUrl);
    } catch (err) {
      toast.error(err.message || 'Failed to load image');
    }
  }, [onChange]);

  return (
    <div
      onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
      onDragLeave={() => setDragOver(false)}
      onDrop={(e) => { e.preventDefault(); setDragOver(false); takeFile(e.dataTransfer.files?.[0]); }}
      data-testid={testid}
      className={`relative border-2 ${dragOver ? 'border-orange-500 bg-orange-50' : value ? 'border-slate-300' : 'border-dashed border-slate-300 hover:border-slate-400'} rounded-lg overflow-hidden bg-slate-50 transition-colors`}
    >
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={(e) => { takeFile(e.target.files?.[0]); e.target.value = ''; }}
      />
      {value ? (
        <div className="relative group">
          <img src={value} alt={label} className="w-full h-40 object-cover" />
          <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition flex items-center justify-center gap-2">
            <button type="button" onClick={() => inputRef.current?.click()} className="px-3 py-1.5 bg-white/90 hover:bg-white rounded text-sm font-semibold text-slate-800">
              <Upload size={14} className="inline mr-1" /> Replace
            </button>
            <button type="button" onClick={() => onChange(null)} className="px-3 py-1.5 bg-red-600 hover:bg-red-700 rounded text-sm font-semibold text-white">
              <X size={14} className="inline mr-1" /> Remove
            </button>
          </div>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          className="w-full h-40 flex flex-col items-center justify-center text-slate-500 hover:text-slate-700"
        >
          <ImageIcon size={28} className="mb-2" />
          <span className="text-xs font-semibold uppercase tracking-wider">{label}</span>
          <span className="text-[10px] mt-1 text-slate-400">click or drop image</span>
        </button>
      )}
    </div>
  );
};

const SectionCard = ({ title, description, children }) => (
  <Card className="mb-4">
    <CardHeader className="pb-3">
      <CardTitle className="text-lg">{title}</CardTitle>
      {description && <CardDescription>{description}</CardDescription>}
    </CardHeader>
    <CardContent className="space-y-4">{children}</CardContent>
  </Card>
);

const FieldReportBuilder = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = !!id;

  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(isEdit);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewHtml, setPreviewHtml] = useState('');

  const now = new Date();
  const [form, setForm] = useState({
    campaign_id: null,
    issue_no: 1,
    month_year: `${MONTHS[now.getMonth()]} ${now.getFullYear()}`,
    category: CATEGORIES[0],
    headline: '',
    intro_p1: '',
    intro_p2: '',
    intro_p3: '',
    hero_caption: '',
    stat_1_val: '', stat_1_label: 'Project Cost',
    stat_2_val: '', stat_2_label: 'Total Duration',
    stat_3_val: '', stat_3_label: 'Downtime',
    initial_headline: '',
    initial_intro: '',
    bullet_1: '', bullet_2: '', bullet_3: '', bullet_4: '',
    before_caption: '',
    solution_headline: '',
    solution_p1: '', solution_p2: '',
    step_1_title: 'Surface Preparation', step_1_body: '',
    step_2_title: '', step_2_body: '',
    step_3_title: '', step_3_body: '',
    step_4_title: 'Cure & Return to Service', step_4_body: '',
    outcome_sentence: '',
    photos: {}, // { hero, before_1, before_2, step_1..step_4 } → data URL / URL
    also_create_blog_post: true,
    blog_excerpt: '',
    status: 'draft',
  });

  const upd = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const updPhoto = (slot, dataUrl) => setForm(f => ({ ...f, photos: { ...f.photos, [slot]: dataUrl } }));

  // On mount: fetch next issue # for new reports, or existing meta for edits
  useEffect(() => {
    (async () => {
      try {
        if (isEdit) {
          const res = await axios.get(`${API}/api/crm/campaigns/field-report/${id}/meta`);
          if (res.data.meta) {
            setForm(f => ({ ...f, ...res.data.meta, campaign_id: id }));
          } else {
            setForm(f => ({ ...f, campaign_id: id }));
          }
        } else {
          const res = await axios.get(`${API}/api/crm/campaigns/field-report/next-issue`);
          upd('issue_no', res.data.next_issue);
        }
      } catch (err) {
        // If offline / DB down, keep sensible defaults
        console.warn('Could not fetch initial data:', err?.message);
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  const submit = async (status) => {
    if (!form.headline?.trim()) {
      toast.error('Please add a headline before saving');
      return;
    }
    setSaving(true);
    try {
      const res = await axios.post(`${API}/api/crm/campaigns/field-report/build`, { ...form, status });
      const { campaign, blog_post } = res.data;
      toast.success(
        status === 'draft'
          ? `Field Report saved as draft${blog_post ? ' + blog post' : ''}`
          : `Field Report published${blog_post ? ' + blog post' : ''}`
      );
      // Route back to Campaigns
      navigate('/admin?tab=campaigns');
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const openPreview = async () => {
    // Preview endpoint renders HTML without persisting — no DB writes
    try {
      setSaving(true);
      const res = await axios.post(`${API}/api/crm/campaigns/field-report/preview`, form);
      setPreviewHtml(res.data.html);
      setPreviewOpen(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Preview failed');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="animate-spin text-slate-400" size={32} />
      </div>
    );
  }

  const [monthName, monthYearYear] = (form.month_year || '').split(' ');

  return (
    <div className="min-h-screen bg-slate-50" data-testid="field-report-builder-page">
      {/* Header bar */}
      <div className="sticky top-0 z-30 bg-white border-b border-slate-200 shadow-sm">
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate('/admin?tab=campaigns')} data-testid="fr-back-btn">
              <ArrowLeft size={16} className="mr-1" /> Back
            </Button>
            <div>
              <div className="text-xs uppercase tracking-widest text-orange-600 font-bold">Field Report Builder</div>
              <div className="text-lg font-bold text-slate-900">
                {isEdit ? 'Edit' : 'New'} Field Report {form.issue_no ? `#${form.issue_no}` : ''}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={openPreview} disabled={saving} data-testid="fr-preview-btn">
              <Eye size={14} className="mr-1" /> Preview
            </Button>
            <Button variant="outline" size="sm" onClick={() => submit('draft')} disabled={saving} data-testid="fr-save-draft-btn">
              <Save size={14} className="mr-1" /> Save as Draft
            </Button>
            <Button size="sm" className="bg-orange-600 hover:bg-orange-700" onClick={() => submit('active')} disabled={saving} data-testid="fr-generate-btn">
              {saving ? <Loader2 className="animate-spin mr-1" size={14} /> : <Save size={14} className="mr-1" />} Generate Field Report
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-6">

        {/* ── Section 1: Report Header ─────────────── */}
        <SectionCard title="1 · Report Header" description="Basics about this issue — auto-populated but editable.">
          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label htmlFor="fr-issue-no">Issue Number</Label>
              <Input id="fr-issue-no" data-testid="fr-issue-no" type="number" min="1" value={form.issue_no} onChange={(e) => upd('issue_no', parseInt(e.target.value || '1', 10))} />
            </div>
            <div>
              <Label>Month</Label>
              <Select value={monthName || MONTHS[now.getMonth()]} onValueChange={(v) => upd('month_year', `${v} ${monthYearYear || currentYear}`)}>
                <SelectTrigger data-testid="fr-month"><SelectValue /></SelectTrigger>
                <SelectContent>{MONTHS.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Year</Label>
              <Select value={String(monthYearYear || currentYear)} onValueChange={(v) => upd('month_year', `${monthName || MONTHS[now.getMonth()]} ${v}`)}>
                <SelectTrigger data-testid="fr-year"><SelectValue /></SelectTrigger>
                <SelectContent>{YEARS.map(y => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label>Category (kicker line above the headline)</Label>
            <Select value={form.category} onValueChange={(v) => upd('category', v)}>
              <SelectTrigger data-testid="fr-category"><SelectValue /></SelectTrigger>
              <SelectContent>{CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </SectionCard>

        {/* ── Section 2: Featured Project ─────────────── */}
        <SectionCard title="2 · Featured Project" description="Headline and lead. Three short paragraphs work best.">
          <div>
            <Label htmlFor="fr-headline">Headline *</Label>
            <Input id="fr-headline" data-testid="fr-headline" value={form.headline} onChange={(e) => upd('headline', e.target.value)} placeholder="e.g., Overnight composite repair of a leaking Victaulic flange." />
          </div>
          <div><Label>Intro paragraph 1</Label><Textarea data-testid="fr-intro-p1" rows={3} value={form.intro_p1} onChange={(e) => upd('intro_p1', e.target.value)} placeholder="Set the scene — project setting, asset, the challenge." /></div>
          <div><Label>Intro paragraph 2</Label><Textarea data-testid="fr-intro-p2" rows={3} value={form.intro_p2} onChange={(e) => upd('intro_p2', e.target.value)} placeholder="What BCS did at a high level, and why it was the right call." /></div>
          <div><Label>Intro paragraph 3</Label><Textarea data-testid="fr-intro-p3" rows={2} value={form.intro_p3} onChange={(e) => upd('intro_p3', e.target.value)} placeholder="The outcome, in one line." /></div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Hero photo</Label>
              <PhotoDrop slot="hero" label="Hero — completed repair" value={form.photos.hero} onChange={(v) => updPhoto('hero', v)} testid="fr-photo-hero" />
            </div>
            <div>
              <Label>Hero caption</Label>
              <Input data-testid="fr-hero-caption" value={form.hero_caption} onChange={(e) => upd('hero_caption', e.target.value)} placeholder="e.g., Completed Belzona Super Wrap II composite repair" />
            </div>
          </div>
        </SectionCard>

        {/* ── Section 3: Project Results (stats) ─────────────── */}
        <SectionCard title="3 · Project Results" description="Three headline stats — value + label each.">
          <div className="grid grid-cols-3 gap-4">
            {[1, 2, 3].map(n => (
              <div key={n} className="space-y-2">
                <Label>Stat {n}</Label>
                <Input data-testid={`fr-stat${n}-val`} value={form[`stat_${n}_val`]} onChange={(e) => upd(`stat_${n}_val`, e.target.value)} placeholder={n === 1 ? '~$12,500' : n === 2 ? 'Overnight' : 'Zero'} />
                <Input data-testid={`fr-stat${n}-label`} value={form[`stat_${n}_label`]} onChange={(e) => upd(`stat_${n}_label`, e.target.value)} placeholder={n === 1 ? 'Project Cost' : n === 2 ? 'Total Duration' : 'Downtime'} />
              </div>
            ))}
          </div>
        </SectionCard>

        {/* ── Section 4: Initial Condition ─────────────── */}
        <SectionCard title="4 · Initial Condition" description="What was wrong — subhead, intro, four bullets, and up to two before photos.">
          <div><Label>Subhead</Label><Input data-testid="fr-initial-headline" value={form.initial_headline} onChange={(e) => upd('initial_headline', e.target.value)} placeholder="e.g., A live leak on a critical process pump." /></div>
          <div><Label>Intro paragraph</Label><Textarea data-testid="fr-initial-intro" rows={3} value={form.initial_intro} onChange={(e) => upd('initial_intro', e.target.value)} placeholder="Set the scene for the failure — what was inspected and why action was needed." /></div>
          <div className="grid grid-cols-2 gap-3">
            {[1, 2, 3, 4].map(n => (
              <div key={n}><Label>Bullet {n}</Label><Input data-testid={`fr-bullet${n}`} value={form[`bullet_${n}`]} onChange={(e) => upd(`bullet_${n}`, e.target.value)} placeholder={`Finding ${n}`} /></div>
            ))}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <PhotoDrop slot="before_1" label="Before photo 1" value={form.photos.before_1} onChange={(v) => updPhoto('before_1', v)} testid="fr-photo-before-1" />
            <PhotoDrop slot="before_2" label="Before photo 2" value={form.photos.before_2} onChange={(v) => updPhoto('before_2', v)} testid="fr-photo-before-2" />
          </div>
          <div><Label>Before-photos caption</Label><Input data-testid="fr-before-caption" value={form.before_caption} onChange={(e) => upd('before_caption', e.target.value)} placeholder="e.g., Documented leak at the Victaulic flange · pre-repair" /></div>
        </SectionCard>

        {/* ── Section 5: Engineered Solution ─────────────── */}
        <SectionCard title="5 · Engineered Solution" description="The repair method — subhead and two supporting paragraphs.">
          <div><Label>Subhead</Label><Input data-testid="fr-solution-headline" value={form.solution_headline} onChange={(e) => upd('solution_headline', e.target.value)} placeholder="e.g., Belzona Super Wrap II — a bonded composite repair." /></div>
          <div><Label>Paragraph 1</Label><Textarea data-testid="fr-solution-p1" rows={3} value={form.solution_p1} onChange={(e) => upd('solution_p1', e.target.value)} placeholder="Explain the method, product system, and why it was chosen." /></div>
          <div><Label>Paragraph 2</Label><Textarea data-testid="fr-solution-p2" rows={3} value={form.solution_p2} onChange={(e) => upd('solution_p2', e.target.value)} placeholder="Operational advantage — cold-applied, no hot work, in-service, etc." /></div>
        </SectionCard>

        {/* ── Section 6: Repair Process (4 steps) ─────────────── */}
        <SectionCard title="6 · Repair Process" description="Four steps — title, description, and one photo each.">
          {[1, 2, 3, 4].map(n => (
            <div key={n} className="border rounded p-4 space-y-3 bg-white">
              <div className="text-xs font-bold uppercase tracking-widest text-orange-600">Step {String(n).padStart(2, '0')}</div>
              <div className="grid grid-cols-3 gap-3">
                <div className="col-span-1">
                  <PhotoDrop slot={`step_${n}`} label={`Step ${n} photo`} value={form.photos[`step_${n}`]} onChange={(v) => updPhoto(`step_${n}`, v)} testid={`fr-photo-step-${n}`} />
                </div>
                <div className="col-span-2 space-y-2">
                  <div><Label>Step title</Label><Input data-testid={`fr-step${n}-title`} value={form[`step_${n}_title`]} onChange={(e) => upd(`step_${n}_title`, e.target.value)} /></div>
                  <div><Label>Step description</Label><Textarea data-testid={`fr-step${n}-body`} rows={3} value={form[`step_${n}_body`]} onChange={(e) => upd(`step_${n}_body`, e.target.value)} placeholder="What was done in this step — SSPC standards, products, technique, etc." /></div>
                </div>
              </div>
            </div>
          ))}
        </SectionCard>

        {/* ── Section 7: Outcome ─────────────── */}
        <SectionCard title="7 · Outcome" description="One punchy line that lands the win.">
          <Textarea data-testid="fr-outcome" rows={2} value={form.outcome_sentence} onChange={(e) => upd('outcome_sentence', e.target.value)} placeholder="e.g., Leak eliminated overnight · pump stayed in service · multi-day shutdown avoided for ~$12,500." />
        </SectionCard>

        {/* ── Section 8: Publishing options ─────────────── */}
        <SectionCard title="8 · Publishing Options">
          <div className="flex items-start gap-3 py-2">
            <Checkbox
              id="fr-also-blog"
              data-testid="fr-also-blog"
              checked={form.also_create_blog_post}
              onCheckedChange={(v) => upd('also_create_blog_post', !!v)}
            />
            <div>
              <Label htmlFor="fr-also-blog" className="cursor-pointer">Also create a matching blog post at <code>/research-learn/&lt;slug&gt;</code></Label>
              <p className="text-xs text-slate-500 mt-1">The blog post uses a cleaner web layout (not the email tables) and links from the newsletter to the full case study.</p>
            </div>
          </div>
          {form.also_create_blog_post && (
            <div>
              <Label>Blog excerpt (optional)</Label>
              <Textarea data-testid="fr-blog-excerpt" rows={2} value={form.blog_excerpt} onChange={(e) => upd('blog_excerpt', e.target.value)} placeholder="Short teaser shown in blog listings. Defaults to the first intro paragraph." />
            </div>
          )}
        </SectionCard>

        {/* Bottom action row (mirror of top) */}
        <div className="flex justify-end gap-2 mt-6 pb-16">
          <Button variant="outline" onClick={openPreview} disabled={saving}><Eye size={14} className="mr-1" /> Preview</Button>
          <Button variant="outline" onClick={() => submit('draft')} disabled={saving}><Save size={14} className="mr-1" /> Save as Draft</Button>
          <Button className="bg-orange-600 hover:bg-orange-700" onClick={() => submit('active')} disabled={saving}>{saving ? <Loader2 className="animate-spin mr-1" size={14} /> : <Save size={14} className="mr-1" />} Generate Field Report</Button>
        </div>
      </div>

      {/* Preview modal — wide enough to show 620px email without horizontal scroll */}
      {previewOpen && (
        <div className="fixed inset-0 bg-black/60 z-40 flex items-center justify-center p-4" onClick={() => setPreviewOpen(false)}>
          <div className="bg-white rounded-lg shadow-2xl w-full max-w-4xl max-h-[92vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-4 py-3 border-b bg-slate-50">
              <div>
                <div className="text-xs uppercase tracking-widest text-orange-600 font-bold">Email Preview</div>
                <div className="font-bold text-slate-900">Field Report #{form.issue_no} · {form.headline || '[Headline]'}</div>
              </div>
              <button onClick={() => setPreviewOpen(false)} className="text-slate-500 hover:text-slate-800 p-1" data-testid="fr-preview-close"><X size={22} /></button>
            </div>
            <iframe
              title="preview"
              srcDoc={`<!doctype html><html><head><meta charset="utf-8"><meta name="viewport" content="width=device-width, initial-scale=1"></head><body style="margin:0; padding:0;">${previewHtml}</body></html>`}
              className="flex-1 w-full bg-slate-100"
              style={{ minHeight: '75vh' }}
              data-testid="fr-preview-iframe"
            />
          </div>
        </div>
      )}

    </div>
  );
};

export default FieldReportBuilder;
